import {IStudentsService} from './IStudentsService';
import {
  InitEnvironmentVariable,
  InitWithAmplify,
  StatisticService,
  UpdateStudentInput,
  User,
  UserService as PikadoUserService,
} from '@PikaDoApp/npm-pikado-logic';
import {Amplify} from 'aws-amplify';
import {getEnvironment} from '../../lib/getEnvironment';

export class StudentsService implements IStudentsService {
  private pikadoUserService: PikadoUserService;
  private statisticService: StatisticService;
  private userByIdCache = new Map<string, Promise<any>>();
  private studentByIdCache = new Map<string, Promise<any>>();

  constructor() {
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.pikadoUserService = new PikadoUserService();
    this.statisticService = new StatisticService();
  }

  private normalizeUser(user: User | any): any {
    if (!user) {
      return undefined;
    }

    return {
      ...user,
      id: user.id ?? user._id ?? '',
      parent: user.parent ?? null,
    };
  }

  private normalizeStudent(student: any, user?: any): any {
    if (!student) {
      return null;
    }

    const id = student.id ?? student._id ?? '';

    return {
      ...student,
      id,
      _id: student._id ?? id,
      classschoolId: student.classschoolId ?? student.classSchoolId,
      classSchoolId: student.classSchoolId ?? student.classschoolId,
      classschool: student.classschool ?? {
        id: student.classschoolId ?? student.classSchoolId,
      },
      school: student.school ?? {
        id: student.schoolId,
      },
      level: student.level ?? {
        id: student.levelId,
      },
      grade: student.grade ?? {
        id: student.gradeId,
      },
      user: user ?? student.user,
      memberships: student.memberships ?? [],
      statistic: student.statistic,
      sessions: student.sessions ?? [],
    };
  }

  private normalizeStudentsConnection(
    items: any[],
    totalCount: number,
    nextToken: string | null,
  ): any {
    return {
      items,
      nextToken,
      scannedCount: items.length,
      totalCount,
      __typename: 'StudentsConnection',
    };
  }

  async getUserById(userId: string): Promise<any> {
    if (!userId) {
      return undefined;
    }

    if (!this.userByIdCache.has(userId)) {
      this.userByIdCache.set(
        userId,
        this.pikadoUserService
          .getUserById(userId)
          .then(user => this.normalizeUser(user))
          .catch(error => {
            this.userByIdCache.delete(userId);
            throw error;
          }),
      );
    }

    return await this.userByIdCache.get(userId)!;
  }

  async getSessionsStudentById(studentId: string): Promise<any> {
    const student = await this.getStudentById(studentId);
    return {
      ...student,
      sessions: [],
    };
  }

  async getStudents(
    nextToken: string | null,
    searchKey?: string,
    searchValue?: string,
    limit: number = 100,
  ): Promise<any> {
    const offset = nextToken ? Number(nextToken) : 0;

    const response = await this.pikadoUserService.findAllStudents(
      limit,
      offset,
      searchKey === 'NAME' ? searchValue : undefined,
    );

    let items = response?.items ?? [];

    if (searchValue?.length) {
      const query = searchValue.toLowerCase();

      if (searchKey === 'ID') {
        items = items.filter(student => {
          const id = student?._id ?? '';
          return id.toLowerCase().includes(query);
        });
      } else if (searchKey === 'NAME') {
        items = items.filter(student => {
          const name = student?.name ?? '';
          return name.toLowerCase().includes(query);
        });
      }
    }

    const normalizedItems = items.map(student =>
      this.normalizeStudent(student),
    );

    const canPaginate =
      !searchValue &&
      offset + normalizedItems.length < (response?.totalCount ?? 0);

    return this.normalizeStudentsConnection(
      normalizedItems,
      searchValue ? normalizedItems.length : (response?.totalCount ?? 0),
      canPaginate ? String(offset + normalizedItems.length) : null,
    );
  }

  async getStudentById(studentId: string): Promise<any> {
    if (!studentId) {
      return null;
    }

    if (!this.studentByIdCache.has(studentId)) {
      this.studentByIdCache.set(
        studentId,
        (async () => {
          const student =
            await this.pikadoUserService.getStudentById(studentId);
          const user = student?.userId
            ? await this.getUserById(student.userId).catch(() => null)
            : null;

          return this.normalizeStudent(student, user);
        })().catch(error => {
          this.studentByIdCache.delete(studentId);
          throw error;
        }),
      );
    }

    return await this.studentByIdCache.get(studentId)!;
  }

  async getStudentWithFillReportByStudentId(studentId: string): Promise<any> {
    const student = await this.getStudentById(studentId);
    const studentStats =
      await this.statisticService.getStudentStatisticsByStudentId(studentId);
    const statisticsByStudent =
      await this.statisticService.getStatisticsByStudentId(studentId);

    const monthlyReports = (
      studentStats?.studentPastMonthlyAverageList ?? []
    ).map(entry => ({
      id: `${studentId}-${entry.month}`,
      month: entry.month,
      average: entry.average ?? 0,
      attempts: 0,
      mistakes: 0,
      absenceDays: 0,
    }));

    const phoneticsAsString = (
      studentStats?.studentLifeTimePhonetics ?? []
    ).map(item => ({
      phone: item.phone,
      mistakes: item.mistakes,
      attempts: item.attempts,
      score: item.score,
    }));

    const syllablesAsString = (
      studentStats?.studentLifeTimeSyllables ?? []
    ).map(item => ({
      letters: item.syllable,
      mistakes: item.mistakes,
      attempts: item.attempts,
      score: item.score,
    }));

    const scoreList = (statisticsByStudent?.[0]?.scores ?? [])
      .map(scoreEntry => scoreEntry?.score)
      .filter(score => typeof score === 'number');

    return {
      ...student,
      statistic: {
        id: statisticsByStudent?.[0]?.id ?? student?.id,
        averageScore: studentStats?.studentLifeTimeAverage ?? 0,
        fullReportLastGeneratedDate: new Date().toISOString(),
        scores: scoreList,
        phoneticsAsString,
        syllablesAsString,
        monthlyReports,
      },
    };
  }

  async getStudentsByClassId(classId: string): Promise<any> {
    const students = await this.pikadoUserService.getStudentsByClassId(classId);
    return (students ?? []).map(student => this.normalizeStudent(student));
  }

  async updateStudent(student: any): Promise<any> {
    const input: UpdateStudentInput = {
      id: student.id ?? student._id,
      name: student.name,
      schoolId: student.schoolId ?? student.school?.id,
      classSchoolId:
        student.classSchoolId ??
        student.classschoolId ??
        student.classschool?.id,
      levelId: student.levelId ?? student.level?.id,
      gradeId: student.gradeId ?? student.grade?.id,
      gender: student.gender,
      birthDate: student.birthDate,
      age: student.age ? Number(student.age) : undefined,
    };

    const response = await this.pikadoUserService.updateStudent(input);
    return this.normalizeStudent(response, student.user);
  }

  async removeStudentFromClass(student: any): Promise<any> {
    const input: UpdateStudentInput = {
      id: student.id ?? student._id,
      schoolId: null,
      classSchoolId: null,
    };

    const response = await this.pikadoUserService.updateStudent(input);
    return this.normalizeStudent(response, student.user);
  }

  async getSyllablesByLetters(lettersList: Array<string>): Promise<any> {
    return await this.statisticService.getSyllablesByLetters(lettersList);
  }

  async generateStudentFullReport(
    studentId: string,
    _statisticId: string,
  ): Promise<any> {
    return await this.statisticService.generateFullReportAsString(studentId);
  }

  async createStudent(
    studentUserId: string,
    name: string,
    birthDate: string,
    age: number,
    gender: string,
    schoolId: string,
    _isSchoolVerfied: boolean,
    classschoolId: string,
  ): Promise<any> {
    const response = await this.pikadoUserService.createStudent({
      userId: studentUserId,
      name,
      birthDate,
      age,
      gender: gender as any,
      schoolId,
      classSchoolId: classschoolId,
    });

    return this.normalizeStudent(response);
  }
}
